/**
 * App.tsx - Main Application Component
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './services/firebaseConfig';
import { doc, updateDoc, increment, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getAuthHeaders, getAuthHeadersForFormData } from './services/apiAuth';
import { getApiBaseUrl, isNativePlatform } from './utils/apiConfig';
import { CapacitorHttp } from '@capacitor/core';
// Admin components
import InventoryManager from './components/admin/InventoryManager';
// Checkout components
import OrderHistory from './components/checkout/OrderHistory';
import CheckoutForm from './components/checkout/CheckoutForm';
// Modal components
import FaceGuidelineModal from './components/modals/FaceGuidelineModal';
import type { AnalysisProvider } from './components/modals/FaceGuidelineModal';
import SettingsModal from './components/modals/SettingsModal';
import PhotoConsentModal from './components/modals/PhotoConsentModal';
import NotificationPromptModal, {
  hasUserDismissedNotificationPrompt,
  markNotificationPromptDismissed,
} from './components/modals/NotificationPromptModal';
import WelcomeModal from './components/modals/WelcomeModal';
import LanguageRegionModal from './components/modals/LanguageRegionModal';
// Layout components
import PhotoUploadOptions from './components/layout/PhotoUploadOptions';
import PhaseIndicator from './components/layout/PhaseIndicator';
import TopBar from './components/layout/TopBar';
import LoginForm from './components/layout/LoginForm';
import FeedbackForm from './components/feedback/FeedbackForm';
// Chat components
import ChatInput from './components/chat/ChatInput';
import ChatMessageList from './components/chat/ChatMessageList';
import ChatStarterPrompts from './components/chat/ChatStarterPrompts';
import UsageMeter from './components/chat/UsageMeter';
import type { UsageMeterHandle } from './components/chat/UsageMeter';
// Common components
import StatusBanners from './components/common/StatusBanners';
import CountryConfirmBanner from './components/common/CountryConfirmBanner';
import ToastNotification from './components/common/ToastNotification';
// Profile components
import MobileProfileFAB from './components/profile/MobileProfileFAB';
import DesktopProfilePanel from './components/profile/DesktopProfilePanel';
// Cart components
import ResponsiveCart from './components/cart/ResponsiveCart';
// Product components
import ProductExperiencePanel from './components/product/ProductExperiencePanel';
import {
  extractProfile,
  applyProfileDiff,
  mergeProfile,
  loadProfile,
  saveProfile,
  ensureShape,
  enforceUSDefaults,
  calculateProfileCompletion,
} from './services/profileService';
import { isInPhase1 } from './utils/phaseUtils';
import { getCatalog } from './services/catalogService';
import { useConversation } from './hooks/useConversation';
import ChatNudgeBanner from './components/chat/ChatNudgeBanner';
import { saveOpenAIConversationId } from './services/chatHistoryService';
import { useResponsive } from './hooks/useResponsive';
import { useLanguage } from './hooks/useLanguage';
import { useCheckout } from './hooks/useCheckout';
import { usePhotoAnalysis } from './hooks/usePhotoAnalysis';
import { useWebSearch } from './hooks/useWebSearch';
import { useGoogleAuth } from './hooks/useGoogleAuth';
import { SUPPORTED_LANGUAGES, changeLanguage } from './i18n';
import type { SupportedLanguage } from './i18n';
import { useProfileManager } from './hooks/useProfileManager';
import { useCart } from './hooks/useCart';
import { useEditorPicks } from './hooks/useEditorPicks';
import { useWishlist } from './hooks/useWishlist';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { useSponsoredSlots } from './hooks/useSponsoredSlots';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useVoiceSession } from './hooks/useVoiceSession';
import { detectSearchIntent } from './utils/searchIntentDetector';
import { extractProfileFromVoice, mergeVoiceExtractionIntoProfile } from './utils/voiceProfileExtractor';
import { processAnalysisResult, AIAnalysisResult } from './utils/aiAnalysisUtils';
import { streamChatResponse } from './utils/streamChatResponse';
import { sanitizeChatMessages } from './utils/messageSanitizer';
import { loadProductEmbeddings } from './services/embeddingService';
import { MONETIZATION_MODE, RECOMMENDATION_MODE, INHOUSE_SELLING_ENABLED, SAMPLE_FEATURE_ENABLED } from './config/adminConfig';
import type { MasterProfile, LanguageCode } from './types';
import type { ChatMessage, ProfileConflict, RecentChanges, WebSearchResultItem, SearchTriggerSource } from './types/conversation';
import type { NormalizedProduct } from './services/catalogService';
import './App.css';
import { initActivityTracking, trackMessageCount, setupUnloadHandler, trackProductInteraction, updateLastVisitDate } from './services/activityTrackingService';
import { trackAffiliateClick } from './services/eventTrackingService';
import { detectUserRegion, getRegionConfig, REGION_CONFIG } from './config/affiliateConfig';
import type { SupportedRegion } from './config/affiliateConfig';
import { isInAppBrowser } from './services/socialAuthService';

/**
 * Extract recent search context from messages for AI awareness
 * Finds the most recent web-search message and extracts product info
 */
function extractSearchContextFromMessages(messages: ChatMessage[]): {
  recentSearches: Array<{
    name: string;
    title: string;
    link?: string;
    source?: 'inhouse' | 'search' | 'web';
    price?: string | number;
    reason?: string;
  }>;
  lastSearchKeywords?: string[];
} | undefined {
  // Find the most recent web-search message (reverse order)
  const webSearchMessage = [...messages].reverse().find(
    msg => msg.type === 'web-search' && msg.webSearchResults?.length
  );
  
  if (!webSearchMessage?.webSearchResults) {
    return undefined;
  }
  
  // Extract up to 5 most relevant results for AI context
  const recentSearches = webSearchMessage.webSearchResults.slice(0, 5).map(result => ({
    name: result.name || 'Unknown Product',
    title: result.name || 'Unknown Product',
    link: result.link,
    source: result.source as 'inhouse' | 'search' | 'web' | undefined,
    price: result.price,
    reason: result.reason,
  }));
  
  return {
    recentSearches,
    // Keywords could be extracted from the message text if needed
  };
}

function App() {
  // Auth-derived userId — reactive state for hooks that need uid (cart, wishlist).
  // auth.currentUser?.uid is NOT reactive; this listener guarantees re-render.
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid ?? null);
      // Reset per-session feedback flag so nudge works for the new user
      setFeedbackSubmittedThisSession(false);
    });
    return () => unsub();
  }, []);

  // Auth state - local state synced with useGoogleAuth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecking, setAuthChecking] = useState(true); // Show loading while checking auth
  const [isSigningIn, setIsSigningIn] = useState(false); // Show loading during sign-in process
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const usageMeterRef = useRef<UsageMeterHandle>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const shouldAutoScrollRef = useRef(true);
  
  // Rate limit cooldown — seconds remaining until the user can send again
  const [rateLimitCooldown, setRateLimitCooldown] = useState(0);
  const rateLimitTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => { if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current); };
  }, []);
  
  // Daily budget exhaustion — disables send/search proactively (before hitting 429)
  const [dailyAiRemaining, setDailyAiRemaining] = useState<number | null>(null);
  const [dailySearchRemaining, setDailySearchRemaining] = useState<number | null>(null);
  const handleBudgetChange = useCallback((aiRemaining: number, searchRemaining: number) => {
    setDailyAiRemaining(aiRemaining);
    setDailySearchRemaining(searchRemaining);
  }, []);
  
  // Ref for search handler - allows voice mode to trigger search.
  // Declared before the voice hook so the hook can read it, but assigned after useWebSearch
  // is initialized to avoid a circular dependency between the voice hook and the search handler.
  const handleSearchRef = useRef<((messages: ChatMessage[], hasUserInteracted: boolean, triggerSource?: SearchTriggerSource, userQueryText?: string) => Promise<void>) | null>(null);
  
  // Ref for profile update - allows voice mode to use same backend extraction as text mode.
  // Same pattern as handleSearchRef - declared early, assigned after updateProfileFromMessages is defined.
  const updateProfileRef = useRef<((conversation: ChatMessage[]) => Promise<void>) | null>(null);
  
  // Ref to track latest messages - avoids anti-pattern of using setMessages callback just to read state
  const messagesRef = useRef<ChatMessage[]>([]);
  
  /**
   * Voice Turn State Machine
   * Manages the ordering of user transcript and AI response in voice mode.
   * 
   * States:
   * - 'idle': No active voice turn (ready for new speech)
   * - 'listening': User is speaking (speech_started received)
   * - 'processing': User stopped speaking, waiting for transcript
   * - 'transcribed': User transcript received, waiting for/received AI response
   * 
   * Transitions:
   * - idle → listening (onSpeechStart)
   * - listening → processing (implicit, when speech stops)
   * - processing → transcribed (onTranscript final)
   * - transcribed → idle (after AI response added)
   * 
   * Buffering:
   * - If AI response arrives while in 'listening' or 'processing', buffer it
   * - When transcript arrives, add user message then flush buffer
   */
  type VoiceTurnState = 'idle' | 'listening' | 'processing' | 'transcribed';
  const voiceTurnState = useRef<VoiceTurnState>('idle');
  const pendingVoiceAIResponse = useRef<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const [masterProfile, setMasterProfile] = useState<Partial<MasterProfile>>(() => ensureShape({}));
  const [recentChanges, setRecentChanges] = useState<RecentChanges | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [profileDiff, setProfileDiff] = useState<Record<string, any> | null>(null);
  const [profileConflicts, setProfileConflicts] = useState<ProfileConflict[]>([]);
  const [coppaBlocked, setCoppaBlocked] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [catalog, setCatalog] = useState<NormalizedProduct[]>([]);
  const [autoSave, setAutoSave] = useState(true);
  const [autoSearchEnabled, setAutoSearchEnabled] = useState(true); // On by default — auto-search when AI detects product intent
  const [useRagRecommendation, setUseRagRecommendation] = useState(RECOMMENDATION_MODE === 'rag');
  const [monetizationMode, setMonetizationMode] = useState(MONETIZATION_MODE);
  const [showAddressFormForSample, setShowAddressFormForSample] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmittedThisSession, setFeedbackSubmittedThisSession] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<WebSearchResultItem | null>(null);
  const [showWishlistPanel, setShowWishlistPanel] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showLangRegionSetup, setShowLangRegionSetup] = useState(false);
  
  // Banner visibility state — holistic notification management
  // Initialize regionMismatchDismissed to true to avoid one-render flash; useEffect will set to false if active mismatch exists
  const [regionMismatchDismissed, setRegionMismatchDismissed] = useState(true);
  // Lazy-init from localStorage to avoid one-render flash for users who already dismissed
  const [phaseIndicatorDismissed, setPhaseIndicatorDismissed] = useState(() => {
    try {
      const key = userId ? `heseda_phase_indicator_dismissed_${userId}` : 'heseda_phase_indicator_dismissed';
      const data = localStorage.getItem(key);
      if (!data) return false;
      const { dismissedAt } = JSON.parse(data);
      return Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  // Recompute phaseIndicatorDismissed when userId changes (login/logout/account switch)
  useEffect(() => {
    try {
      const key = userId ? `heseda_phase_indicator_dismissed_${userId}` : 'heseda_phase_indicator_dismissed';
      const data = localStorage.getItem(key);
      if (!data) { setPhaseIndicatorDismissed(false); return; }
      const { dismissedAt } = JSON.parse(data);
      setPhaseIndicatorDismissed(Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000);
    } catch { setPhaseIndicatorDismissed(false); }
  }, [userId]);
  
  const [sessionId] = useState(() => {
    let storedSessionId = localStorage.getItem('sessionId');
    if (!storedSessionId) {
      storedSessionId = uuidv4();
      localStorage.setItem('sessionId', storedSessionId);
    }
    return storedSessionId;
  });

  // OpenAI Conversations API — persistent server-side conversation state
  const openAIConversationIdRef = useRef<string | undefined>(undefined);

  // === Hooks ===
  const { language, setLanguage, t, ti18n } = useLanguage();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  useDocumentMeta({
    title: ti18n('appTitle'),
    description: ti18n('appDescription'),
    path: currentPath === '/chat' ? '/chat' : '/',
  });
  const {
    messages,
    setMessages,
    input,
    setInput,
    hasUserInteracted,
    setHasUserInteracted,
    handleLanguageChange: hookHandleLanguageChange,
    openAIConversationId: restoredOpenAIConversationId,
    nudgeContent,
    dismissNudge,
    historyLoaded,
  } = useConversation({ masterProfile, language, isLoggedIn, sessionId });

  // Clear stale in-memory conversation ID when auth/session context changes
  useEffect(() => {
    openAIConversationIdRef.current = undefined;
  }, [isLoggedIn, sessionId]);

  // Keep the ref aligned with the latest Firestore-restored conversation ID
  useEffect(() => {
    openAIConversationIdRef.current = restoredOpenAIConversationId;
  }, [restoredOpenAIConversationId]);

  const { isCompact } = useResponsive();

  // Cart hook - manages cart items, toast notifications, cart visibility
  // Now syncs with Firebase for logged-in users
  const {
    cartItems,
    setCartItems,
    showCart,
    setShowCart,
    toastMessage,
    setToastMessage,
    handleAddToCart,
    handleUpdateCartQuantity,
    handleRemoveFromCart,
    cartItemCount,
    clearCart,
  } = useCart({ language, userId });

  // Wishlist hook - manages "My Picks" saved products
  const {
    wishlistItems,
    wishlistCount,
    toggleWishlist,
    removeFromWishlist,
    isInWishlist,
  } = useWishlist({
    language,
    userId,
    onToast: setToastMessage,
  });

  // Editor picks hook - manages AI-based product recommendations
  // In affiliate mode: fetches Amazon products; in inhouse mode: ranks in-house catalog
  const {
    editorPicks,
    setEditorPicks,
    isEditorPicksLoading,
    handleRefreshEditorPicks,
    handleRefreshEditorPicksRef,
    isAffiliate: isEditorPicksAffiliate,
  } = useEditorPicks({
    masterProfile,
    catalog,
    isLoggedIn,
    t,
    setProfileError,
    monetizationMode,
    useRag: useRagRecommendation,
  });

  // Sponsored products hook - affiliate/Amazon products (6 tiles)
  const {
    sponsoredProducts,
    isLoading: isSponsoredLoading,
    trackClick: trackSponsoredClick,
  } = useSponsoredSlots({
    maxSlots: 6,
    skinType: masterProfile?.skin?.type,
    concerns: masterProfile?.skin?.concerns,
    region: 'ALL',
    autoFetch: true,
    enableAffiliateFallback: true,
    masterProfile: masterProfile as MasterProfile,
  });

  // Session timeout hook - auto sign out after 30 min inactivity or 24h absolute max
  useSessionTimeout(auth.currentUser, {
    inactivityTimeout: 30 * 60 * 1000, // 30 minutes
    absoluteSessionMax: 24 * 60 * 60 * 1000, // 24 hours
    onTimeout: () => {
      setToastMessage(ti18n('common:session.expiredInactivity'));
    },
    onAbsoluteTimeout: () => {
      setToastMessage(ti18n('common:session.expiredSecurity'));
    },
  });

  // Push notifications hook - for native apps (Android/iOS) only
  // Web browsers don't support Capacitor push, so this is a no-op on web
  const { 
    isSupported: isPushSupported, 
    isEnabled: isPushEnabled,
    permissionStatus: pushPermissionStatus,
    requestPermission: requestPushPermission,
  } = usePushNotifications({
    isLoggedIn,
    profileCompletion: calculateProfileCompletion(masterProfile),
    onNotificationReceived: (notification) => {
      // Show toast for foreground notifications
      setToastMessage(notification.title || 'New notification');
    },
    onNotificationAction: (action) => {
      // Handle notification tap actions
      console.log('📱 Handling notification action:', action);
      switch (action) {
        case 'open_profile':
          setIsProfileOpen(true);
          break;
        case 'open_cart':
          setShowCart(true);
          break;
        case 'open_chat':
          // Chat is the main view, just ensure profile is closed
          setIsProfileOpen(false);
          setShowCart(false);
          break;
        case 'open_orders':
          // Navigate to settings/orders tab - for now just open profile
          setIsProfileOpen(true);
          break;
        default:
          // Default: just open the app (no special action)
          break;
      }
    },
  });

  // Phase 2 unlock toast — fires once when profile crosses from <50% to >=50%
  const prevCompletionRef = useRef<number | null>(null);
  useEffect(() => {
    const completion = calculateProfileCompletion(masterProfile);
    if (prevCompletionRef.current !== null && prevCompletionRef.current < 50 && completion >= 50) {
      setToastMessage(language === 'ko'
        ? '🎉 프로필 완성! 🔍 검색 버튼으로 맞춤 제품을 찾아보세요!'
        : '🎉 Profile complete! Click 🔍 Search to find products!');
    }
    prevCompletionRef.current = completion;
  }, [masterProfile, language, setToastMessage]);

  // Region mismatch banner auto-dismiss — show once per unique mismatch, auto-hide after 10s.
  // Resets if the mismatch state changes (e.g., user travels to a new country).
  const regionMismatchKeyRef = useRef<string>('');
  useEffect(() => {
    const profileCountry = masterProfile?.identity?.country;
    if (!profileCountry) return;
    const region = detectUserRegion(profileCountry);
    const rc = getRegionConfig(region);
    const langMismatch = rc.hl !== language && !rc.hl.startsWith(language);
    if (!langMismatch) { setRegionMismatchDismissed(true); regionMismatchKeyRef.current = ''; return; }
    const mismatchKey = `${region}:${language}`;
    // Per-user scoped key to avoid cross-account leakage on shared devices
    const storageKey = userId ? `heseda_region_mismatch_dismissed_${userId}_${mismatchKey}` : `heseda_region_mismatch_dismissed_${mismatchKey}`;
    // Check if this specific mismatch was already dismissed
    try {
      if (localStorage.getItem(storageKey) === 'true') {
        setRegionMismatchDismissed(true);
        return;
      }
    } catch { /* */ }
    // New mismatch — show banner and auto-dismiss after 10s
    // Include userId in dedupe key to handle account switches on shared devices
    const dedupeKey = userId ? `${userId}:${mismatchKey}` : mismatchKey;
    if (regionMismatchKeyRef.current !== dedupeKey) {
      regionMismatchKeyRef.current = dedupeKey;
      setRegionMismatchDismissed(false);
      const timer = setTimeout(() => {
        setRegionMismatchDismissed(true);
        try { localStorage.setItem(storageKey, 'true'); } catch { /* */ }
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [masterProfile?.identity?.country, language, userId]);

  // Only clear COPPA block when we can confirm the user is at least 13 from explicit age data.
  // ageGroup alone is not sufficient because under-13 users may still be classified as "teens".
  useEffect(() => {
    const ageGroup = masterProfile?.identity?.ageGroup;
    const birthYear = masterProfile?.identity?.birthYear;

    if (!coppaBlocked || !ageGroup || !['teens', '20s', '30s', '40s', '50s', '60s+'].includes(ageGroup)) {
      return;
    }

    // For non-teens age groups, we can safely clear (20s+ is always >= 13)
    if (ageGroup !== 'teens') {
      setCoppaBlocked(false);
      return;
    }

    // For teens, require birthYear confirmation that age >= 13
    const numericBirthYear = typeof birthYear === 'number' ? birthYear : Number(birthYear);
    if (!Number.isFinite(numericBirthYear)) {
      return;
    }

    const currentYear = new Date().getFullYear();
    const age = currentYear - numericBirthYear;
    if (age >= 13) {
      setCoppaBlocked(false);
    }
  }, [masterProfile?.identity?.ageGroup, masterProfile?.identity?.birthYear, coppaBlocked]);

  // Show language/region setup modal for first-time users who haven't explicitly chosen.
  // Blocks before WelcomeModal so the AI language is set correctly from the start.
  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    // Skip if already completed (per-user localStorage key)
    try {
      if (localStorage.getItem(`heseda_langregion_completed_${userId}`)) {
        setShowLangRegionSetup(false);
        return;
      }
    } catch { /* storage blocked */ }
    // Skip if the user already confirmed their country (returning user on new device)
    if (masterProfile?.identity?.countryConfirmed) {
      setShowLangRegionSetup(false);
      try { localStorage.setItem(`heseda_langregion_completed_${userId}`, 'true'); } catch { /* */ }
      return;
    }
    const timer = setTimeout(() => setShowLangRegionSetup(true), 400);
    return () => clearTimeout(timer);
  }, [isLoggedIn, userId, masterProfile?.identity?.countryConfirmed]);

  // Show welcome modal for first-time users after login
  // Scoped per-user and coordinated with notification prompt and lang/region modal to avoid stacking.
  // Also checks Firestore masterProfile for prior activity so the modal doesn't
  // reappear when the same user logs in from a new browser/device.
  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    // Wait until language/region setup is done
    if (showLangRegionSetup) return;
    try {
      if (localStorage.getItem(`heseda_onboarding_completed_${userId}`)) return;
    } catch { /* storage blocked — fall through to Firestore check */ }

    // If Firestore profile already has skin data, the user has onboarded before —
    // persist the flag locally so we don't check again and skip the modal.
    // Check both current skin.* fields and legacy root-level aiAnalysis for
    // migrated accounts that haven't been fully restructured yet.
    const hasExistingProfile = !!(
      masterProfile.skin?.type ||
      masterProfile.skin?.concerns?.length ||
      masterProfile.skin?.aiAnalysis?.skinType ||
      masterProfile.aiAnalysis?.skinType
    );
    if (hasExistingProfile) {
      try { localStorage.setItem(`heseda_onboarding_completed_${userId}`, 'true'); } catch { /* */ }
      return;
    }

    const timer = setTimeout(() => {
      if (!showNotificationPrompt) setShowWelcome(true);
    }, 600);
    return () => clearTimeout(timer);
  }, [isLoggedIn, userId, showNotificationPrompt, showLangRegionSetup, masterProfile.skin, masterProfile.aiAnalysis]);

  // Show notification prompt after login on native platforms
  // Only shows if: native platform + logged in + permission not yet granted + not previously dismissed
  // Waits until language/region setup and welcome modal are both finished to avoid modal stacking.
  useEffect(() => {
    if (
      isPushSupported &&
      isLoggedIn &&
      !showLangRegionSetup &&
      !showWelcome &&
      pushPermissionStatus !== 'granted' &&
      pushPermissionStatus !== 'unknown' && // Wait for status check to complete
      !hasUserDismissedNotificationPrompt()
    ) {
      // Small delay to let the login UI settle before showing another modal
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isPushSupported, isLoggedIn, pushPermissionStatus, showLangRegionSetup, showWelcome]);

  // Handlers for notification prompt modal
  const handleEnableNotifications = useCallback(async () => {
    setShowNotificationPrompt(false);
    const granted = await requestPushPermission();
    if (granted) {
      setToastMessage(ti18n('modals:notificationPrompt.enabled'));
    }
  }, [requestPushPermission, setToastMessage, ti18n]);

  const handleDismissNotificationPrompt = useCallback(() => {
    setShowNotificationPrompt(false);
    markNotificationPromptDismissed();
  }, []);

  // Extract search context from messages for voice AI
  // This ensures voice AI has same context as text AI (architecture consistency)
  const voiceSearchContext = useMemo(() => 
    extractSearchContextFromMessages(messages),
    [messages]
  );

  // Keep messagesRef in sync with messages state
  // This avoids the anti-pattern of using setMessages callback just to read state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  /**
   * Delay before triggering profile extraction from voice callbacks.
   * Allows message state to settle before extraction runs.
   */
  const PROFILE_EXTRACTION_DELAY_MS = 100;

  /**
   * Schedule backend profile extraction from the current conversation.
   * Used by both onTranscript and onAIResponse to avoid code duplication.
   */
  const scheduleProfileExtraction = useCallback(() => {
    setTimeout(() => {
      if (updateProfileRef.current) {
        updateProfileRef.current(messagesRef.current);
      }
    }, PROFILE_EXTRACTION_DELAY_MS);
  }, []);

  // Voice language: always honor the user's chosen UI language.
  // If they selected English, voice should be English — no silent region fallback.
  const voiceLanguage = language;

  // Voice session hook - OpenAI Realtime API for voice mode
  const {
    state: voiceState,
    isSupported: isVoiceSupported,
    toggle: toggleVoice,
    sendText: sendVoiceText,
    updateSearchContext: updateVoiceSearchContext,
  } = useVoiceSession({
    language: voiceLanguage,
    masterProfile,
    profileCompletion: calculateProfileCompletion(masterProfile),
    searchContext: voiceSearchContext, // Same search context as text AI uses
    onTranscript: (text, isFinal) => {
      if (isFinal) {
        // Transition: processing → transcribed
        voiceTurnState.current = 'transcribed';
        
        // Show user's transcribed speech in chat with 'voice' source
        // Then flush any buffered AI response (maintains correct order)
        setMessages(prev => {
          const newMessages = [...prev, { 
            id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
            timestamp: new Date().toISOString(),
            sender: 'user' as const, 
            text, 
            source: 'voice' as const 
          }];
          
          // If AI response arrived before transcript, add it now (after user message)
          if (pendingVoiceAIResponse.current) {
            newMessages.push({ 
              id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
              timestamp: new Date().toISOString(),
              sender: 'bot' as const, 
              text: pendingVoiceAIResponse.current, 
              source: 'voice' as const 
            });
            pendingVoiceAIResponse.current = null;
            // Turn complete
            voiceTurnState.current = 'idle';
            trackMessageCount(2); // user + buffered bot
          } else {
            trackMessageCount(1); // user only (bot counted when it arrives)
          }
          
          return newMessages;
        });
        
        // Quick client-side extraction for instant feedback
        // voiceProfileExtractor only supports ko/en patterns; for other
        // voice languages, fall back to 'en' (backend GPT extraction handles all languages)
        const extractorLang: 'ko' | 'en' = voiceLanguage === 'ko' ? 'ko' : 'en';
        const voiceExtraction = extractProfileFromVoice(text, extractorLang);
        if (voiceExtraction.hasExtractions) {
          const { profile: updatedProfile, changed, changes } = mergeVoiceExtractionIntoProfile(
            masterProfile,
            voiceExtraction
          );
          if (changed) {
            setMasterProfile(enforceUSDefaults(updatedProfile) as MasterProfile);
            // Notify user of profile updates (toast uses UI language)
            const changeMsg = language === 'ko' 
              ? `프로필 업데이트: ${changes.join(', ')}`
              : `Profile updated: ${changes.join(', ')}`;
            setToastMessage(changeMsg);
          }
        }
        
        // Backend extraction for full feature parity (same as text mode)
        // This provides: GPT extraction, "NEW" badges, auto-save, undo support
        scheduleProfileExtraction();
        
        // Check for explicit search intent (e.g., "검색해줘", "추천해줘")
        // Use voiceLanguage so patterns match the transcript's actual language
        const profileCompletion = calculateProfileCompletion(masterProfile);
        const shouldSearch = detectSearchIntent(text, voiceLanguage, profileCompletion);
        
        console.log('[Voice] Search intent check:', { shouldSearch, hasSearchRef: !!handleSearchRef.current, text });
        
        if (shouldSearch) {
          // Notify user that voice triggered a product search
          setToastMessage(language === 'ko' ? '🔍 음성으로 제품 검색 중...' : '🔍 Searching products from voice...');
          // Trigger search after brief delay (let AI respond first, and ensure ref is ready)
          setTimeout(() => {
            if (handleSearchRef.current) {
              // Use messagesRef to access latest messages without setState callback anti-pattern
              // Let extractBoldKeywords chain build curated queries from AI response
              handleSearchRef.current(messagesRef.current, true, 'voice-auto');
            } else {
              console.warn('[Voice] handleSearchRef not ready, search skipped');
            }
          }, 500);
        }
      }
    },
    onAIResponse: (text, isFinal) => {
      if (isFinal) {
        const state = voiceTurnState.current;
        
        // Check current turn state to determine buffering
        if (state === 'listening' || state === 'processing') {
          // User transcript not yet received - buffer AI response
          pendingVoiceAIResponse.current = text;
          console.log('[Voice] Buffering AI response (state:', state, ')');
        } else {
          // State is 'transcribed' or 'idle' - safe to add directly
          setMessages(prev => [...prev, { 
            id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
            timestamp: new Date().toISOString(),
            sender: 'bot', 
            text, 
            source: 'voice' 
          }]);
          trackMessageCount(1); // bot response (user already counted in onTranscript)
          // Turn complete
          voiceTurnState.current = 'idle';
        }
        
        // Backend extraction from AI response (same as text mode)
        // AI often clarifies/confirms profile info - capture that too
        scheduleProfileExtraction();
      }
    },
    onSpeechStart: () => {
      // Transition: idle → listening
      // User started speaking - new turn begins
      voiceTurnState.current = 'listening';
      pendingVoiceAIResponse.current = null;
    },
    onError: (error) => {
      setToastMessage(`Voice error: ${error}`);
    },
    onInactivityTimeout: () => {
      const msg = language === 'ko' 
        ? '음성 세션이 비활성으로 종료되었습니다 (60초)' 
        : 'Voice session ended due to inactivity (60s)';
      setToastMessage(msg);
    },
  });

  const {
    showCheckoutForm,
    setShowCheckoutForm,
    checkoutData,
    handleCartCheckout
  } = useCheckout({ cartItems, setCartItems, setShowCart, setToastMessage, language, clearCart });

  const {
    selectedImage,
    imagePreviewUrl,
    showPhotoConsent,
    setShowPhotoConsent,
    showFaceGuideline,
    setShowFaceGuideline,
    showPhotoOptions,
    setShowPhotoOptions,
    shouldAutoSend,
    resetAutoSend,
    handlePhotoConsentAccept,
    handleCameraCapture,
    handleImageChange,
    requestCameraPermission,
    clearPhotoState,
    analysisProvider,
    setAnalysisProvider,
  } = usePhotoAnalysis({ masterProfile, setMasterProfile });

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, [setMessages]);

  const {
    isSearching,
    clearWebResults,
    handleSearch: hookHandleSearch,
  } = useWebSearch({
    language,
    sessionId,
    masterProfile,
    catalog,
    addMessage,
    handleRefreshEditorPicksRef,
    t,
    useRag: useRagRecommendation,
    // Sync search results to voice session for context awareness
    onSearchComplete: updateVoiceSearchContext,
  });

  // Populate search ref for voice mode access
  useEffect(() => {
    handleSearchRef.current = hookHandleSearch;
  }, [hookHandleSearch]);

  // Auto-search on fresh login: run a search once when a returning user
  // logs in, but skip if the restored conversation already has recent
  // search results (page reload / tab restore). Uses sessionStorage to
  // prevent duplicate searches within the same browser session.
  const loginSearchFiredRef = useRef(false);
  useEffect(() => {
    // Reset on logout so next login can trigger; also clear the
    // sessionStorage flag so a same-tab re-login fires a fresh search
    // regardless of which sign-out path was used (Settings, session
    // timeout, admin sign-out, etc.).
    if (!isLoggedIn) {
      loginSearchFiredRef.current = false;
      try {
        // auth.currentUser is already null at this point, so iterate
        // sessionStorage keys to find and remove any login-search flag.
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key?.startsWith('heseda-login-search-')) {
            sessionStorage.removeItem(key);
          }
        }
      } catch { /* sessionStorage unavailable */ }
      return;
    }
    if (!historyLoaded || loginSearchFiredRef.current) return;
    if (!autoSearchEnabled) return;

    // Only Phase 2 users (completion >= 50%) qualify for search
    const completion = calculateProfileCompletion(masterProfile);
    if (completion < 50) return;

    // Skip if conversation already has web-search results (reload / restore)
    const hasExistingSearchResults = messages.some(
      m => m.type === 'web-search' && m.webSearchResults && m.webSearchResults.length > 0,
    );
    if (hasExistingSearchResults) {
      loginSearchFiredRef.current = true;
      return;
    }

    // Skip if already auto-searched in this browser session
    const sessionKey = `heseda-login-search-${auth.currentUser?.uid}`;
    try {
      if (sessionStorage.getItem(sessionKey)) {
        loginSearchFiredRef.current = true;
        return;
      }
    } catch { /* sessionStorage unavailable */ }

    loginSearchFiredRef.current = true;

    // Mark this browser session so page refreshes don't re-trigger
    try { sessionStorage.setItem(sessionKey, '1'); } catch { /* */ }

    console.log('🔍 Login auto-search: triggering fresh product search');
    const timerId = setTimeout(() => {
      // Re-check auth state; user may have logged out during the delay
      if (!auth.currentUser) return;
      hookHandleSearch(messages, true, 'login-auto').catch(err =>
        console.error('Login auto-search failed:', err),
      );
    }, 1000); // brief delay to let profile state settle

    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, historyLoaded]);

  useEffect(() => {
    if (shouldAutoSend && selectedImage && imagePreviewUrl) {
      resetAutoSend();
      handleSend();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoSend, selectedImage, imagePreviewUrl]);

  // Sync language from Firebase profile on login — only on the FIRST load
  // when the user has not yet explicitly chosen a language.  The dedicated
  // flag 'heseda-ai-language-explicit' distinguishes a real user choice from
  // the auto-cached locale that i18next's LanguageDetector writes to
  // localStorage on every visit.
  useEffect(() => {
    if (!masterProfile?.identity?.countryConfirmed) return;
    const savedLanguage = masterProfile?.identity?.uiLanguage;
    if (!savedLanguage || !(SUPPORTED_LANGUAGES as readonly string[]).includes(savedLanguage)) return;

    // If the user explicitly chose a language in this browser, keep it.
    try {
      if (localStorage.getItem('heseda-ai-language-explicit') === 'true') return;
    } catch { /* private browsing / blocked storage */ }

    // No explicit local choice yet — apply the Firebase value and mark it.
    if (savedLanguage !== language) {
      setLanguage(savedLanguage as LanguageCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterProfile?.identity?.uiLanguage, masterProfile?.identity?.countryConfirmed]);

  useEffect(() => {
    (async () => {
      try {
        // Load catalogs in parallel and merge
        // TODO: Need to move the dataset to DB and call API in future.
        // Only load catalogs when in-house selling is enabled;
        // both heseda-catalog and kbeauty-catalog are in-house products
        // that should not appear in search/picks in affiliate-only mode.
        if (INHOUSE_SELLING_ENABLED) {
          const [inhouseItems, kbeautyItems] = await Promise.all([
            getCatalog('/catalogs/heseda-catalog.csv').catch(() => []),
            getCatalog('/catalogs/kbeauty-catalog.csv').catch(() => []),
          ]);
          // Tag genuine K-beauty brands for origin-aware ranking/boost
          // (catalog also contains non-Korean brands like Clinique, The Ordinary, etc.)
          const KBEAUTY_BRAND_NAMES = ['cosrx', 'sk-ii', 'klairs', 'missha', 'purito', 'laneige', 'innisfree', 'beauty of joseon', 'etude', 'sulwhasoo', 'banila co', 'by wishtrend', 'krave beauty'];
          const taggedKbeauty = (kbeautyItems || []).map(p => ({
            ...p,
            isKBeauty: KBEAUTY_BRAND_NAMES.some(kb => (p.brand || '').toLowerCase().includes(kb)),
          }));
          setCatalog([...(inhouseItems || []), ...taggedKbeauty]);
        } else {
          setCatalog([]);
        }
      } catch (error) {
        console.error('Failed to load catalog:', error);
        setCatalog([]);
      }
      
      // Load product embeddings for vector-based matching (only needed for in-house catalog)
      if (INHOUSE_SELLING_ENABLED) {
        try {
          await loadProductEmbeddings();
        } catch (error) {
          console.warn('Product embeddings not loaded:', error);
        }
      }
    })();
  }, []);

  const resetProfileState = useCallback(() => {
    const emptyProfile = ensureShape({});
    setMasterProfile(emptyProfile);
    setRecentChanges(null);
    setProfileDiff(null);
    setProfileConflicts([]);
    setCoppaBlocked(false);
    setProfileError(null);
    setEditorPicks([]);
  }, [setEditorPicks]);

  const hydrateProfile = useCallback(async (uid: string, forceServer: boolean = true) => {
    if (!uid) {
      return;
    }

    setIsProfileLoading(true);
    try {
      const data = await loadProfile(uid, forceServer);
      const shaped = enforceUSDefaults(ensureShape(data || {}));

      // Backfill identity.email from Firebase auth if missing in Firestore
      const currentUser = auth.currentUser;
      if (currentUser?.email && !shaped.identity?.email) {
        shaped.identity = { ...shaped.identity, email: currentUser.email };
        const profileRef = doc(db, 'masterProfiles', uid);
        setDoc(profileRef, {
          identity: { email: currentUser.email },
          meta: { updatedAt: serverTimestamp() },
        }, { merge: true }).catch(err => console.warn('⚠️ Email backfill failed:', err));
      }

      setMasterProfile(shaped);
      setRecentChanges(null);
      setProfileDiff(null);
      setProfileConflicts([]);
      setCoppaBlocked(false);
      setProfileError(null);
    } catch (error) {
      console.error('Failed to load master profile:', error);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally omitting ti18n: it's an unstable reference that recreates every render, causing infinite loops when included. The translation function's output is stable for the same key.
      setProfileError(ti18n('common:errors.profileLoadFailed'));
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  /**
   * Confirm auto-detected country — saves to Firestore and updates local state.
   */
  const handleCountryConfirm = useCallback(async (region: SupportedRegion) => {
    const user = auth.currentUser;
    if (!user) return;
    if (!REGION_CONFIG[region]) return;
    try {
      const rc = getRegionConfig(region);
      const profileRef = doc(db, 'masterProfiles', user.uid);
      await updateDoc(profileRef, {
        'identity.country': region,
        'identity.currency': rc.currency,
        'identity.countryConfirmed': true,
        'meta.updatedAt': serverTimestamp(),
      });
      setMasterProfile((prev) => ({
        ...prev,
        identity: { ...prev.identity, country: region, currency: rc.currency, countryConfirmed: true },
      }));
    } catch (err) {
      console.error('Failed to confirm country:', err);
    }
  }, []);

  // Handle language/region modal confirmation — persist choices and dismiss
  const handleLangRegionConfirm = useCallback(async (lang: SupportedLanguage, region: SupportedRegion) => {
    // Always set the explicit flag and dismiss modal, regardless of Firestore errors.
    // This prevents the language-sync effect from reverting the user's choice.
    try { localStorage.setItem('heseda-ai-language-explicit', 'true'); } catch { /* */ }
    try {
      await changeLanguage(lang);
    } catch (err) {
      console.error('Language change failed (non-critical):', err);
    }
    try {
      await handleCountryConfirm(region);
    } catch (err) {
      console.error('Country confirm failed (non-critical):', err);
    }
    // Persist to Firestore (best-effort — may fail with mock auth or offline)
    try {
      const user = auth.currentUser;
      if (user) {
        const profileRef = doc(db, 'masterProfiles', user.uid);
        await updateDoc(profileRef, { 'identity.uiLanguage': lang });
      }
    } catch (err) {
      console.error('Firestore uiLanguage update failed (non-critical):', err);
    }
    // Update uiLanguage in local state
    setMasterProfile((prev) => ({
      ...prev,
      identity: { ...prev.identity, uiLanguage: lang },
    }));
    // Mark onboarding complete
    try { if (userId) localStorage.setItem(`heseda_langregion_completed_${userId}`, 'true'); } catch { /* */ }
    setShowLangRegionSetup(false);
  }, [userId, handleCountryConfirm]);

  // Google Auth hook - manages Firebase auth state, sign-in, and profile population
  const {
    handleGoogleSignIn,
    setManualSignIn,
  } = useGoogleAuth({ 
    hydrateProfile, 
    resetProfileState,
    setIsLoggedIn,
    setLoginError,
    setAuthChecking,
    setIsSigningIn,
  });

  // Profile management hook - handles profile updates and AI analysis storage
  const {
    handleUpdateProfile,
    updateMasterDBWithAnalysis,
    masterProfileRef,
  } = useProfileManager({
    masterProfile,
    setMasterProfile,
    showAddressFormForSample,
    setShowAddressFormForSample,
    language,
    t,
  });

  const updateProfileFromMessages = useCallback(async (conversation: ChatMessage[]) => {
    const user = auth.currentUser;
    if (!user || !Array.isArray(conversation) || conversation.length === 0) {
      return;
    }

    setIsProfileLoading(true);
    try {
      const resp = await extractProfile({
        messages: conversation,
        currentProfile: masterProfileRef.current || {},
        language,
      });

      const diff = resp && typeof resp.diff === 'object' && resp.diff ? resp.diff : null;
      
      if (diff && Object.keys(diff).length > 0) {
        // Save previous profile for undo
        const previousProfile = { ...masterProfileRef.current };
        
        // Apply diff directly to masterProfile
        const updatedProfile = enforceUSDefaults(applyProfileDiff(masterProfileRef.current || {}, diff));
        setMasterProfile(updatedProfile);
        setProfileDiff(diff);
        
        // Store for undo
        setRecentChanges({
          timestamp: Date.now(),
          diff,
          previousProfile
        });
        
        // Auto-save to Firestore if enabled
        if (autoSave) {
          if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
          }
          autoSaveTimerRef.current = setTimeout(async () => {
            try {
              await saveProfile(user.uid, updatedProfile);
              
              // Auto-refresh recommendations only in Phase 2 (editor picks aren't shown in Phase 1)
              if (!isInPhase1(calculateProfileCompletion(updatedProfile))) {
                await handleRefreshEditorPicks(updatedProfile);
              }
            } catch (error) {
              console.error('Auto-save failed:', error);
              setProfileError(ti18n('common:errors.profileAutoSaveFailed'));
            }
          }, 3000); // 3 second delay for user to see changes
        }
      } else if (resp?.profile && Object.keys(resp.profile).length > 0) {
        // MERGE profile updates with existing profile (don't replace!)
        // This prevents data loss when API returns profile without diff
        const currentProfile = masterProfileRef.current || {};
        const mergedProfile = mergeProfile(currentProfile, resp.profile);
        const shapedProfile = enforceUSDefaults(ensureShape(mergedProfile));
        setMasterProfile(shapedProfile);
        setProfileDiff(null);
      } else {
        // No changes extracted - keep existing profile
        setProfileDiff(null);
      }

      setProfileConflicts(Array.isArray(resp?.conflicts) ? resp.conflicts : []);
      setCoppaBlocked(Boolean(resp?.coppaBlock));
      setProfileError(null);
    } catch (error) {
      console.warn('extractProfile failed', error);
    } finally {
      setIsProfileLoading(false);
    }
  }, [language, autoSave, t, handleRefreshEditorPicks, masterProfileRef]);

  // Populate profile update ref for voice mode access (same pattern as handleSearchRef)
  useEffect(() => {
    updateProfileRef.current = updateProfileFromMessages;
  }, [updateProfileFromMessages]);

  const handleUndo = useCallback(() => {
    if (!recentChanges) {
      return;
    }
    
    // Cancel pending auto-save to prevent undone changes from being persisted
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    
    // Revert to previous profile
    setMasterProfile(enforceUSDefaults(recentChanges.previousProfile));
    setProfileDiff(null);
    setRecentChanges(null);
    setProfileConflicts([]);
    setCoppaBlocked(false);
    setProfileError(null);
  }, [recentChanges]);

  const handleRequestSample = useCallback(() => {
    if (!SAMPLE_FEATURE_ENABLED) return;
    const completion = calculateProfileCompletion(masterProfile);
    
    // Check Phase 2 requirement (35% minimum)
    if (completion < 35) {
      const message = t(
        '샘플을 추천해드리려면 프로필을 조금 더 완성해주세요. 현재 완성도: ' + completion + '%',
        'Please complete your profile a bit more to get sample recommendations. Current: ' + completion + '%'
      );
      alert(message);
      
      // Bot suggests continuing profile
      setMessages(prev => [...prev, {
        id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
        timestamp: new Date().toISOString(),
        sender: 'bot',
        text: t(
          '고객님의 피부에 딱 맞는 샘플을 추천해드리고 싶어요! 피부 타입, 고민, 연령대 등을 알려주시면 맞춤 샘플을 보내드릴게요. 😊',
          'I want to recommend the perfect samples for you! Please tell me about your skin type, concerns, and age to send personalized samples. 😊'
        ),
        isSystemSuggestion: true,
      }]);
      return;
    }
    
    // Check if address exists
    const hasAddress = masterProfile?.logistics?.address1 && 
                       masterProfile?.logistics?.city && 
                       masterProfile?.logistics?.postalCode;
    
    if (!hasAddress) {
      // Show address form modal instead of chat question
      setShowAddressFormForSample(true);
      return;
    }
    
    // Profile complete + address exists → Process sample request
    alert(t(
      '✅ 샘플 요청이 접수되었습니다! 고객님의 피부에 맞는 제품을 선별하여 보내드리겠습니다.',
      '✅ Sample request submitted! We will select products matching your skin profile and send them to you.'
    ));
  }, [masterProfile, t, setMessages]);

  // Format build timestamp to be user-friendly (Date + Time, no seconds)
  const buildTimestamp = (() => {
    const raw = import.meta.env.VITE_BUILD_TIMESTAMP;
    if (!raw || raw === 'Unknown') return 'Dev';
    
    try {
      const date = new Date(raw);
      // Format: "Nov 21, 8:09 AM"
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Dev';
    }
  })();

  useEffect(() => {
    if (!chatBoxRef.current || !shouldAutoScrollRef.current) return;
    const el = chatBoxRef.current;
    try {
      el.scrollTop = el.scrollHeight;
    } catch {
      // ignore scroll issues (mobile browsers may throw)
    }
    requestAnimationFrame(() => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      }
    });
  }, [messages]);

  // Show/hide scroll-to-bottom button based on scroll position
  const handleChatScroll = () => {
    if (!chatBoxRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
    const shouldShow = scrollHeight - scrollTop - clientHeight > 200;
    shouldAutoScrollRef.current = !shouldShow;
    setShowScrollButton(prev => prev === shouldShow ? prev : shouldShow);
  };

  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      requestAnimationFrame(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
      });
      shouldAutoScrollRef.current = true;
      setShowScrollButton(false);
    }
  };

  // Auto-close profile bottom sheet when user sends a message (on mobile)
  const handleSendWithCollapse = async () => {
    // Close bottom sheet on mobile before sending
    if (window.innerWidth <= 768) {
      setIsProfileOpen(false);
    }
    await handleSend();
  };

  // Language change handler - delegates to hook, passes setLanguage
  // Also syncs in-memory masterProfile to prevent stale uiLanguage overwrites
  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLanguage = e.target.value as LanguageCode;
    setMasterProfile((prev) => ({
      ...prev,
      identity: { ...prev.identity, uiLanguage: selectedLanguage },
    }));
    try { localStorage.setItem('heseda-ai-language-explicit', 'true'); } catch { /* */ }
    hookHandleLanguageChange(e, setLanguage);
  };

  const logUserAction = async (userId: string, actionType: string) => {
    try {
      // Phase 1 Step 4: write only to masterProfiles
      const profileDoc = doc(db, 'masterProfiles', userId);
      await setDoc(profileDoc, {
        meta: { [actionType]: increment(1) },
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user actions:', error);
    }
  };

  const handleSend = async (overrideText?: string) => {
    const textToSend = overrideText ?? input;
    if (!textToSend.trim() && !selectedImage) {
      return;
    }

    setHasUserInteracted(true);

    // Web search results now persist inline in chat messages,
    // but we still clear the state for analytics/tracking purposes
    clearWebResults();

    const userMessage: ChatMessage = selectedImage
      ? { 
          sender: 'user', text: '', imageUrl: imagePreviewUrl || undefined,
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
        }
      : { 
          sender: 'user', text: textToSend,
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
        };

    const conversationWithUser: ChatMessage[] = [...messages, userMessage];
    setMessages(conversationWithUser);

    const userInput = textToSend;
    setInput('');
    setIsLoading(true);

    // Dismiss mobile keyboard after send to reclaim screen space
    if (window.innerWidth <= 768 && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    try {
      if (selectedImage) {
        // Log what we're sending
        console.log('📤 Sending image:', selectedImage.name, 'Type:', selectedImage.type, 'Size:', selectedImage.size);

        // Determine which API endpoint to use based on provider selection
        const endpointPath = analysisProvider === 'gpt-vision' 
          ? '/getImageAnalysisByGPTVision'
          : '/getImageAnalysisByAILabTools';
        
        const formDataHeaders = await getAuthHeadersForFormData();
        
        let response: { status: number; data: unknown };
        
        if (isNativePlatform()) {
          // NATIVE: Use CapacitorHttp with base64 data (bypasses CORS)
          // CapacitorHttp doesn't support FormData, so we send base64 JSON
          const apiEndpoint = `${getApiBaseUrl()}${endpointPath}`;
          
          // Convert File to base64 (Array.from is more efficient than loop concatenation)
          const arrayBuffer = await selectedImage.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const binary = Array.from(uint8Array, (byte) => String.fromCharCode(byte)).join('');
          const base64Data = btoa(binary);
          
          const requestHeaders = {
            ...formDataHeaders,
            'Content-Type': 'application/json',
          };
          
          // Log native POST details
          console.log('🌐 Native POST to:', apiEndpoint, 'Base64 length:', base64Data.length);
          
          const httpResponse = await CapacitorHttp.post({
            url: apiEndpoint,
            headers: requestHeaders,
            data: {
              imageBase64: base64Data,
              imageName: selectedImage.name,
              imageType: selectedImage.type,
              sessionId,
              language,
            },
          });
          
          // CapacitorHttp may return data as string - parse it if needed
          let responseData = httpResponse.data;
          if (typeof responseData === 'string') {
            try {
              responseData = JSON.parse(responseData);
            } catch (parseError) {
              console.error('Failed to parse response as JSON:', parseError);
            }
          }
          
          response = { status: httpResponse.status, data: responseData };
          
          if (httpResponse.status >= 400) {
            console.error('HTTP Error:', httpResponse.status, responseData);
            // Create error with response data for proper error handling downstream
            const apiError = new Error(`API request failed: ${httpResponse.status}`) as any;
            apiError.response = { status: httpResponse.status, data: responseData };
            throw apiError;
          }
        } else {
          // WEB: Use standard FormData with fetch (works with relative URLs)
          const apiEndpoint = `/api${endpointPath}`;
          
          const formData = new FormData();
          formData.append('image', selectedImage);
          formData.append('sessionId', sessionId);
          formData.append('language', language);
          
          const fetchResponse = await fetch(apiEndpoint, {
            method: 'POST',
            headers: formDataHeaders,
            body: formData,
          });
          
          if (!fetchResponse.ok) {
            const errorText = await fetchResponse.text();
            throw new Error(`API request failed: ${fetchResponse.status} - ${errorText}`);
          }
          
          response = { status: fetchResponse.status, data: await fetchResponse.json() };
        }

        if (auth.currentUser) {
          await logUserAction(auth.currentUser.uid, 'tokenUsage');
        }

        if (!response || !response.data) {
          throw new Error(`Invalid response from ${analysisProvider}`);
        }

        const analysisResult = response.data as { result?: AIAnalysisResult };

        if (!analysisResult.result) {
          console.error('No result in response:', response.data);
          throw new Error('No analysis result received');
        }

        // Build a prompt with skincare analysis results
        const analysisSummary = processAnalysisResult(analysisResult.result, language);

        // Update Master DB with AI analysis results
        // 1. Skin analysis results.
        await updateMasterDBWithAnalysis(analysisResult.result, language);

        const searchContext = extractSearchContextFromMessages(conversationWithUser);
        // Build Picked for You context for image analysis AI response
        const imageEditorPicksCtx = editorPicks.length > 0 ? {
          picks: editorPicks.slice(0, 6).map(p => ({
            title: p.title || p.name || '',
            brand: p.brand,
            price: p.priceUSD,
            score: p.score,
            reason: p.reason,
            affiliateSource: p.affiliateSource,
          })),
          isAffiliate: isEditorPicksAffiliate,
        } : undefined;
        
        let chatMessageText = '';
        let shouldTriggerSearchFromImage = false;
        let streamMsgId = '';
        
        try {
          // Use streaming for image analysis response for better UX
          console.log('🔄 Starting streaming chat response...');
          streamMsgId = `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`;
          const streamMsgTimestamp = new Date().toISOString();
          const streamingBotMessage: ChatMessage = { 
            id: streamMsgId,
            timestamp: streamMsgTimestamp,
            sender: 'bot', 
            text: '',
            isStreaming: true,
          };
          setMessages([...conversationWithUser, streamingBotMessage]);
          
          const { fullText, triggerSearch, openAIConversationId: newConvId } = await streamChatResponse(
            {
              prompt: analysisSummary,
              sessionId,
              messages: conversationWithUser,
              language,
              profileCompletion: calculateProfileCompletion(masterProfileRef.current),
              masterProfile: masterProfileRef.current,
              searchContext,
              editorPicksContext: imageEditorPicksCtx,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              isCompact,
              openAIConversationId: openAIConversationIdRef.current,
              isImageAnalysis: true,
            },
            (streamedText) => {
              // Update bot message progressively — isStreaming suppresses Firestore persistence
              setMessages(prev => {
                const updated = [...prev];
                if (updated.length > 0) {
                  updated[updated.length - 1] = { 
                    sender: 'bot', 
                    text: streamedText,
                    id: streamMsgId,
                    timestamp: streamMsgTimestamp,
                    isStreaming: true,
                  };
                }
                return updated;
              });
            }
          );
          
          chatMessageText = fullText || '...';
          shouldTriggerSearchFromImage = triggerSearch;
          if (newConvId) {
            openAIConversationIdRef.current = newConvId;
            const uid = auth.currentUser?.uid;
            if (uid) saveOpenAIConversationId(uid, sessionId, newConvId).catch(() => {});
          }
        } catch (streamError) {
          // Fallback to non-streaming if streaming fails
          console.log('⚠️ Streaming unavailable for image analysis, using standard API');
          
          const authHeaders = await getAuthHeaders();
          const chatResponse = await axios.post('/api/getOpenAIResponse', {
            prompt: analysisSummary,
            sessionId,
            messages: sanitizeChatMessages(conversationWithUser),
            language,
            profileCompletion: calculateProfileCompletion(masterProfileRef.current),
            masterProfile: masterProfileRef.current,
            searchContext,
            editorPicksContext: imageEditorPicksCtx,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            isCompact,
            openAIConversationId: openAIConversationIdRef.current,
            isImageAnalysis: true,
          }, { headers: authHeaders });

          if (!chatResponse || !chatResponse.data) {
            throw new Error('Invalid response from ChatGPT');
          }

          const chatResponseData = chatResponse.data;
          // Guard: reject HTML responses (SPA fallback when API endpoint is unreachable)
          if (typeof chatResponseData === 'string' && chatResponseData.trimStart().startsWith('<!DOCTYPE')) {
            throw new Error('API returned HTML instead of JSON — endpoint may be unreachable');
          }
          if (chatResponseData.openAIConversationId) {
            openAIConversationIdRef.current = chatResponseData.openAIConversationId;
            const uid = auth.currentUser?.uid;
            if (uid) saveOpenAIConversationId(uid, sessionId, chatResponseData.openAIConversationId).catch(() => {});
          }
          chatMessageText = typeof chatResponseData === 'object' 
            ? (chatResponseData.response || chatResponseData.message || String(chatResponseData))
            : String(chatResponseData);
          shouldTriggerSearchFromImage = typeof chatResponseData === 'object' && chatResponseData.triggerSearch;
        }

        // Final update — reuse streaming ID if available so Firestore overwrites the partial
        const finalImgMsgId = streamMsgId
          || `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`;
        const botMessage: ChatMessage = { 
          id: finalImgMsgId,
          timestamp: new Date().toISOString(),
          sender: 'bot', 
          text: chatMessageText 
        };
        const updatedConversation: ChatMessage[] = [...conversationWithUser, botMessage];
        setMessages(updatedConversation);
        updateProfileFromMessages(updatedConversation);

        // Auto-trigger web search if AI detected product search intent from image analysis
        // Note: Using longer delay (1500ms) to ensure profile state has propagated after Firestore update
        if (shouldTriggerSearchFromImage && autoSearchEnabled) {
          setTimeout(() => {
            hookHandleSearch(updatedConversation, true, 'photo-analysis')
              .catch((error) => {
                console.error('Error during automatic search from image analysis:', error);
              })
              .finally(() => {
                usageMeterRef.current?.refresh();
              });
          }, 1500);
        }
      } else {
        // Text chat - try streaming first for faster perceived response
        const searchContext = extractSearchContextFromMessages(conversationWithUser);
        // Build Picked for You context so AI can reference currently displayed products
        const editorPicksCtx = editorPicks.length > 0 ? {
          picks: editorPicks.slice(0, 6).map(p => ({
            title: p.title || p.name || '',
            brand: p.brand,
            price: p.priceUSD,
            score: p.score,
            reason: p.reason,
            affiliateSource: p.affiliateSource,
          })),
          isAffiliate: isEditorPicksAffiliate,
        } : undefined;
        const chatPrompt = language === 'ko'
          ? `당신은 헤세다(Heseda)입니다 — 사용자의 개인 스킨케어 친구이자 전문 컨설턴트입니다. 자기소개를 반복하지 마세요. ${userInput}`
          : `You are Heseda — the user's personal skincare friend and expert consultant. Do not repeat your self-introduction. ${userInput}`;
        const chatProfileCompletion = calculateProfileCompletion(masterProfile);
        
        let messageText = '';
        let shouldTriggerSearch = false;
        let streamMsgId = '';
        
        try {
          // Try streaming endpoint first
          // Add placeholder bot message that updates progressively
          streamMsgId = `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`;
          const streamMsgTimestamp = new Date().toISOString();
          const streamingBotMessage: ChatMessage = { 
            id: streamMsgId,
            timestamp: streamMsgTimestamp,
            sender: 'bot', 
            text: '',
            isStreaming: true,
          };
          setMessages([...conversationWithUser, streamingBotMessage]);
          
          const { fullText, triggerSearch, openAIConversationId: newConvId2 } = await streamChatResponse(
            {
              prompt: chatPrompt,
              sessionId,
              messages: conversationWithUser,
              language,
              profileCompletion: chatProfileCompletion,
              masterProfile: masterProfile,
              searchContext,
              editorPicksContext: editorPicksCtx,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              isCompact,
              openAIConversationId: openAIConversationIdRef.current,
              searchRemaining: dailySearchRemaining,
            },
            (streamedText) => {
              // Update bot message progressively — isStreaming suppresses Firestore persistence
              setMessages(prev => {
                const updated = [...prev];
                if (updated.length > 0) {
                  updated[updated.length - 1] = { 
                    sender: 'bot', 
                    text: streamedText,
                    id: streamMsgId,
                    timestamp: streamMsgTimestamp,
                    isStreaming: true,
                  };
                }
                return updated;
              });
            }
          );
          
          messageText = fullText || '...';
          shouldTriggerSearch = triggerSearch;
          if (newConvId2) {
            openAIConversationIdRef.current = newConvId2;
            const uid = auth.currentUser?.uid;
            if (uid) saveOpenAIConversationId(uid, sessionId, newConvId2).catch(() => {});
          }
        } catch (streamError) {
          // Fallback to non-streaming if streaming fails
          console.log('⚠️ Streaming unavailable, using standard API');
          
          const textAuthHeaders = await getAuthHeaders();
          const response = await axios.post('/api/getOpenAIResponse', {
            prompt: chatPrompt,
            sessionId,
            messages: sanitizeChatMessages(conversationWithUser),
            language,
            profileCompletion: chatProfileCompletion,
            masterProfile: masterProfile,
            searchContext,
            editorPicksContext: editorPicksCtx,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            isCompact,
            openAIConversationId: openAIConversationIdRef.current,
            searchRemaining: dailySearchRemaining,
          }, { headers: textAuthHeaders });

          if (!response || !response.data) {
            throw new Error('Invalid response from ChatGPT');
          }

          const responseData = response.data;
          // Guard: reject HTML responses (SPA fallback when API endpoint is unreachable)
          if (typeof responseData === 'string' && responseData.trimStart().startsWith('<!DOCTYPE')) {
            throw new Error('API returned HTML instead of JSON — endpoint may be unreachable');
          }
          if (responseData.openAIConversationId) {
            openAIConversationIdRef.current = responseData.openAIConversationId;
            const uid = auth.currentUser?.uid;
            if (uid) saveOpenAIConversationId(uid, sessionId, responseData.openAIConversationId).catch(() => {});
          }
          messageText = typeof responseData === 'object' 
            ? (responseData.response || responseData.message || String(responseData))
            : String(responseData);
          shouldTriggerSearch = typeof responseData === 'object' && responseData.triggerSearch;
        }

        // Final update — reuse streaming ID so Firestore overwrites the partial
        const finalMsgId = streamMsgId
          || `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`;
        const botMessage: ChatMessage = { 
          id: finalMsgId,
          timestamp: new Date().toISOString(),
          sender: 'bot', 
          text: messageText 
        };
        const updatedConversation: ChatMessage[] = [...conversationWithUser, botMessage];
        setMessages(updatedConversation);
        updateProfileFromMessages(updatedConversation);

        // Tracking user activity (count only — full text persisted by chatHistoryService)
        trackMessageCount(2); // user message + bot reply

        // Refresh usage meter after every AI text chat
        usageMeterRef.current?.refresh();

        // Auto-trigger web search if AI detected product search intent
        if (shouldTriggerSearch && autoSearchEnabled) {
          setTimeout(() => {
            // Let extractBoldKeywords + profile fallback chain build curated search
            // queries from the AI response (product names in **bold**) rather than
            // passing the raw user message, which produces irrelevant results when
            // the message is conversational (e.g. fiction books in skincare search).
            hookHandleSearch(updatedConversation, true, 'ai-auto')
              .catch((error) => {
                console.error('Auto web search failed:', error);
              })
              .finally(() => {
                usageMeterRef.current?.refresh();
              });
          }, 500);
        }

        if (auth.currentUser) {
          await logUserAction(auth.currentUser.uid, 'chatInitiations');
        }
      }
    } catch (error: any) {
      console.error('❌ handleSend error:', error?.message || error);
      
      // Log error metadata only (not full response data for security)
      console.error('❌ API Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });

      // Detect HTTP status from various error shapes
      const httpStatus = error?.response?.status ?? error?.status;

      // Clean up any empty streaming placeholder left by a failed stream
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.sender === 'bot' && last?.isStreaming && !last?.text) return prev.slice(0, -1);
        return prev;
      });

      // 413 — body too large (chat payload or image upload)
      if (httpStatus === 413 || error?.message?.includes('413')) {
        setToastMessage(ti18n('common:errors.requestTooLarge'));
        return;
      }

      // Detect HTTP 429 (rate limited) — status comes from axios (.response.status)
      // or from streamChatResponse (.status)

      if (httpStatus === 429) {
        // Distinguish daily budget exhaustion from per-minute rate limit
        const dailyLimitCode =
          error?.response?.data?.code ??
          error?.responseData?.code;

        if (dailyLimitCode === 'DAILY_LIMIT') {
          // Daily budget exhausted — no countdown, suggest engagement
          const dailyMsg =
            error?.response?.data?.message ??
            error?.responseData?.message ??
            (language === 'en'
              ? "You've reached your daily AI consultation limit. Explore our product recommendations to earn bonus queries, or come back tomorrow!"
              : '오늘의 AI 상담 한도에 도달했습니다. 제품 추천을 탐색하여 보너스 쿼리를 얻거나, 내일 다시 방문해 주세요!');
          setToastMessage(dailyMsg);
          return; // no countdown timer for daily limits
        }

        // Per-minute sliding window — start countdown timer
        // Parse retryAfter from the response body
        const retryAfter: number =
          error?.response?.data?.retryAfter ??
          error?.responseData?.retryAfter ??
          30; // fallback 30s

        // Start countdown timer
        if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
        setRateLimitCooldown(retryAfter);
        rateLimitTimerRef.current = setInterval(() => {
          setRateLimitCooldown(prev => {
            if (prev <= 1) {
              if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
              rateLimitTimerRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Show toast with rate-limit message (no bot fallback message needed)
        setToastMessage(
          language === 'en'
            ? `You're sending messages too quickly. Please wait ${retryAfter}s.`
            : `메시지를 너무 빠르게 보내고 있습니다. ${retryAfter}초 후 다시 시도해 주세요.`
        );
        return; // skip fallback bot message — toast + cooldown are sufficient
      }

      let fallbackText = language === 'en'
        ? 'Sorry, I could not respond just now. Please try again in a moment.'
        : '지금은 답변을 생성하지 못했습니다. 잠시 후 다시 시도해 주세요.';

      // Extract detailed error message from API response (e.g., face recognition failures)
      if (error?.response?.data) {
        
        let errorData;
        try {
          errorData = typeof error.response.data === 'string' 
            ? JSON.parse(error.response.data) 
            : error.response.data;
        } catch (parseError) {
          console.error('Failed to parse error response:', error.response.data);
          errorData = { error_msg: error.response.data };
        }
        
        // AILabTools error format: error_detail.code_message contains the user-friendly message
        // Also include the error code for debugging
        const errorCode = errorData?.error_detail?.code || errorData?.code;
        const detailedMessage = errorData?.error_detail?.code_message 
          || errorData?.code_message 
          || errorData?.error_msg
          || errorData?.error_detail?.message
          || errorData?.message;
        
        console.error('❌ API error:', { code: errorCode, message: detailedMessage, full: errorData });
        
        if (detailedMessage) {
          // Show the detailed message from AILabTools API
          fallbackText = language === 'en'
            ? `Photo analysis failed: ${detailedMessage}. Please try again with a clearer photo of your face.`
            : `사진 분석 실패: ${detailedMessage}. 얼굴이 잘 보이는 사진으로 다시 시도해 주세요.`;
        }
      }

      const fallbackMessage: ChatMessage = { 
        id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
        sender: 'bot', 
        text: fallbackText,
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages((prevMessages) => [...prevMessages, fallbackMessage]);
    } finally {
      setIsLoading(false);
      clearPhotoState();
      // Refresh usage meter after AI call
      usageMeterRef.current?.refresh();
      // Reset file input value so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /** Callback when user taps "Ask AI" on a product card — auto-sends the question */
  const handleAskAI = (productName: string) => {
    const prompt = language === 'ko'
      ? `${productName}에 대해 알려주세요 — 내 피부에 좋은가요?`
      : `Tell me about ${productName} — is it good for my skin?`;
    handleSend(prompt);
  };

  const handleSearch = useCallback(async () => {
    // Manual search: use typed input if available, otherwise fall back to
    // profile + conversation context (hookHandleSearch extracts keywords from
    // AI analysis, skin profile, bold product names, etc.).
    const userText = input.trim() || undefined;
    await hookHandleSearch(messages, hasUserInteracted, 'manual', userText);
    if (userText) setInput(''); // Clear input after search to avoid stale text
    // Refresh usage meter after search call
    usageMeterRef.current?.refresh();
  }, [hookHandleSearch, messages, hasUserInteracted, input, setInput]);

  /**
   * Track product engagement (view-details or affiliate-click) to earn bonus
   * daily AI/search queries. Fire-and-forget — never blocks UI.
   */
  const trackEngagement = useCallback(
    async (type: 'product-view' | 'affiliate-click', productId: string) => {
      try {
        const headers = await getAuthHeaders();
        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/trackEngagement`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ type, productId }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.granted && data.bonusAmount > 0) {
            setToastMessage(
              language === 'en'
                ? `+${data.bonusAmount} bonus AI queries earned!`
                : `+${data.bonusAmount}개 보너스 AI 쿼리 획득!`,
            );
            // Refresh usage meter to reflect new bonus
            usageMeterRef.current?.refresh();
          }
        }
      } catch {
        // Silently ignore — engagement tracking should never break the UX
      }
    },
    [language, setToastMessage],
  );

  /**
   * Handle "View Details" (상세보기) click from search results
   * Opens ProductExperiencePanel with the selected product
   */
  const handleViewDetails = useCallback((result: WebSearchResultItem) => {
    setSelectedProduct(result);
    // Track product-view engagement (fire & forget)
    if (result.link) {
      trackEngagement('product-view', result.link).catch(() => {});
    }
  }, [trackEngagement]);

  // Show loading screen while checking auth state
  if (authChecking) {
    return (
      <div className="auth-loading-screen">
        <div className="auth-loading-content">
          <img src="/logo-1024_simple.png" alt="" className="auth-loading-logo" width={48} height={48} />
          <p className="auth-loading-brand" aria-hidden="true">Heseda</p>
          <div className="auth-loading-spinner" aria-hidden="true"></div>
          <p role="status" aria-live="polite">{language === 'ko' ? '로딩 중...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoggedIn ? (
      <div className={`chat-container ${isInPhase1(calculateProfileCompletion(masterProfile)) ? 'theme-phase1' : 'theme-phase2'}`}>
        <TopBar
          language={language}
          cartItemCount={cartItemCount}
          wishlistCount={wishlistCount}
          isCompact={isCompact}
          profileCountry={masterProfile?.identity?.country}
          feedbackNudge={!feedbackSubmittedThisSession && messages.filter(m => m.sender === 'user').length >= 5}
          isWishlistOpen={showWishlistPanel}
          onWishlistClick={() => setShowWishlistPanel(prev => !prev)}
          onCartClick={() => setShowCart(true)}
          onSettingsClick={() => setShowSettings(true)}
          onFeedbackClick={() => setShowFeedback(true)}
        />

        {/* In-app browser warning banner (KakaoTalk, Instagram, etc.) */}
        {isInAppBrowser() && (
          <div className="in-app-browser-banner" style={{
            background: '#fef3c7', borderBottom: '1px solid #f59e0b',
            padding: '8px 16px', textAlign: 'center', fontSize: '13px',
            color: '#92400e', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '8px', flexWrap: 'wrap'
          }}>
            <span>{language === 'ko'
              ? '⚠️ 인앱 브라우저에서는 일부 기능이 제한될 수 있습니다.'
              : '⚠️ Some features may be limited in this in-app browser.'}</span>
            <button
              onClick={() => {
                const url = window.location.href;
                window.open(url, '_system');
                try { navigator.clipboard.writeText(url); } catch { /* */ }
              }}
              style={{
                background: '#f59e0b', color: '#fff', border: 'none',
                borderRadius: '6px', padding: '4px 12px', fontSize: '12px',
                fontWeight: 600, cursor: 'pointer'
              }}
            >{language === 'ko' ? '외부 브라우저로 열기' : 'Open in Browser'}</button>
          </div>
        )}

        {/* Cross-language region warning — auto-dismisses after 10s, one-time per mismatch */}
        {(() => {
          if (regionMismatchDismissed) return null;
          const profileCountry = masterProfile?.identity?.country;
          if (!profileCountry) return null;
          const region = detectUserRegion(profileCountry);
          const rc = getRegionConfig(region);
          const langMismatch = rc.hl !== language && !rc.hl.startsWith(language);
          if (!langMismatch) return null;
          const regionName = language === 'ko' ? rc.labelKo : rc.label;
          const langNames: Record<string, string> = {
            en: 'English', ko: '한국어', 'pt-BR': 'Português', es: 'Español',
          };
          const uiLangName = langNames[language] || language;
          const mismatchKey = `${region}:${language}`;
          const storageKey = userId ? `heseda_region_mismatch_dismissed_${userId}_${mismatchKey}` : `heseda_region_mismatch_dismissed_${mismatchKey}`;
          return (
            <div className="region-mismatch-banner region-mismatch-banner--auto-dismiss">
              {language === 'ko'
                ? <>⚠️ 국가가 <strong>{regionName}</strong>(으)로 설정되어 있고 언어는 <strong>{uiLangName}</strong>입니다 — 일부 검색 결과가 제한될 수 있습니다. </>
                : <>⚠️ Your country is set to <strong>{regionName}</strong> but your language is <strong>{uiLangName}</strong> — some product results may be limited. </>
              }
              <button className="region-mismatch-link" onClick={() => setShowSettings(true)}>
                {language === 'ko' ? '설정에서 변경' : 'Change in Settings'}
              </button>
              <button
                type="button"
                className="region-mismatch-close"
                onClick={() => {
                  setRegionMismatchDismissed(true);
                  try { localStorage.setItem(storageKey, 'true'); } catch { /* */ }
                }}
                aria-label="Dismiss"
              >&times;</button>
            </div>
          );
        })()}

        {/* Country confirmation banner — shown once per user after first login */}
        {/* Hidden while LanguageRegionModal is active to prevent its auto-dismiss from */}
        {/* confirming the region prematurely and closing the onboarding modal. */}
        {isLoggedIn && !masterProfile?.identity?.countryConfirmed && !showLangRegionSetup && (
          <CountryConfirmBanner
            detectedRegion={detectUserRegion(undefined)}
            language={language}
            uid={auth.currentUser?.uid}
            onConfirm={handleCountryConfirm}
            onChangeClick={() => setShowSettings(true)}
          />
        )}

        <div className="app-layout">
          <div className="chat-column">
            {/* Status banners - use container with fixed height to prevent layout shift */}
            <StatusBanners
              coppaBlocked={coppaBlocked}
              isProfileLoading={isProfileLoading}
              profileError={profileError}
              language={language}
            />

            {/* Phase Indicator Header — dismissible with 7-day cooldown */}
            {!phaseIndicatorDismissed && (
              <PhaseIndicator
                masterProfile={masterProfile}
                onDismiss={() => {
                  setPhaseIndicatorDismissed(true);
                  try {
                    const key = userId ? `heseda_phase_indicator_dismissed_${userId}` : 'heseda_phase_indicator_dismissed';
                    localStorage.setItem(key, JSON.stringify({ dismissedAt: Date.now() }));
                  } catch { /* */ }
                }}
              />
            )}

            {nudgeContent && (
              <ChatNudgeBanner nudge={nudgeContent} onDismiss={dismissNudge} />
            )}

            <div className="chat-box" ref={chatBoxRef} onScroll={handleChatScroll} aria-live="polite" aria-relevant="additions">
              <ChatMessageList 
                messages={messages} 
                isLoading={isLoading}
                language={language}
                onAddToCart={INHOUSE_SELLING_ENABLED ? handleAddToCart : undefined}
                onWishlistToggle={toggleWishlist}
                isInWishlist={isInWishlist}
                onViewDetails={handleViewDetails}
                onWebResultClick={(result, _index) => {
                  // Track search result click as product interaction for lastVisitDate
                  trackProductInteraction(
                    result.link || result.name || 'unknown',
                    result.name || 'Search Result',
                    'web',
                    'click'
                  );
                  // Track engagement for daily budget bonus
                  if (result.link) {
                    trackEngagement('product-view', result.link).catch(() => {});
                  }
                  // Track as affiliate click so admin dashboard sees web search clicks
                  const uid = auth.currentUser?.uid;
                  if (uid && result.link) {
                    const region = detectUserRegion(masterProfile?.identity?.country);
                    const knownSources = ['amazon', 'coupang', 'iherb', 'yesstyle', 'direct', 'inhouse', 'partner'] as const;
                    const source = knownSources.includes(result.source as typeof knownSources[number])
                      ? (result.source as typeof knownSources[number])
                      : 'direct';
                    trackAffiliateClick(uid, {
                      productId: result.link,
                      productName: result.name || 'Search Result',
                      url: result.link,
                      affiliateSource: source,
                      region,
                      isPaidPlacement: false,
                      placementRow: 'web-search',
                    }).catch(() => {});
                  }
                }}
                sponsoredProducts={sponsoredProducts}
                sponsoredLoading={isSponsoredLoading}
                onSponsoredClick={(product) => {
                  if (product.trackingId) {
                    trackSponsoredClick(product.trackingId).catch((err: Error) =>
                      console.warn('⚠️ Failed to track sponsored click:', err)
                    );
                    // Track affiliate-click engagement for daily budget bonus
                    trackEngagement('affiliate-click', product.trackingId).catch(() => {});
                  }
                  // Track as product interaction so lastVisitDate gets updated
                  trackProductInteraction(
                    product.id,
                    product.name,
                    'partner',
                    'click'
                  );
                  // Geo-routing analytics: track with region + marketplace
                  const uid = auth.currentUser?.uid;
                  if (uid) {
                    const region = detectUserRegion(masterProfile?.identity?.country);
                    trackAffiliateClick(uid, {
                      productId: product.id,
                      productName: product.name,
                      brand: product.brand,
                      url: product.url,
                      affiliateSource: product.affiliateSource,
                      region,
                      isPaidPlacement: true,
                      placementRow: 'sponsored',
                    }).catch(() => {});
                  }
                }}
                onAskAI={handleAskAI}
                onDismissError={(messageId) => {
                  setMessages((prev) => prev.filter((m) => m.id !== messageId));
                }}
              />
              {/* Starter prompts — shown below greeting until user sends first message */}
              {!hasUserInteracted && !isLoading && (
                <ChatStarterPrompts
                  onSelect={(text) => handleSend(text)}
                  profile={masterProfile}
                />
              )}
              {/* Web search results are now rendered inline in chat messages */}
              {/* Row 3: Sponsored/Amazon products now shown via CollapsibleWebResults */}
            </div>

            {showScrollButton && (
              <button
                className="scroll-to-bottom-btn"
                onClick={scrollToBottom}
                aria-label={language === 'ko' ? '맨 아래로 이동' : 'Scroll to bottom'}
              >
                ↓
              </button>
            )}

            <UsageMeter ref={usageMeterRef} language={language} onBudgetChange={handleBudgetChange} />

            <ChatInput
              language={language}
              input={input}
              isLoading={isLoading}
              isSearching={isSearching}
              hasPhotoConsent={!!masterProfile.consent?.photoAnalysisAccepted}
              sampleRequested={
                masterProfile.segment?.customerStage === 'sample-requested' ||
                masterProfile.segment?.customerStage === 'sample-sent' ||
                masterProfile.segment?.customerStage === 'sample-delivered'
              }
              isPhase1={isInPhase1(calculateProfileCompletion(masterProfile))}
              profileCompletion={calculateProfileCompletion(masterProfile)}
              rateLimitCooldown={rateLimitCooldown}
              dailyAiExhausted={dailyAiRemaining === 0}
              dailySearchExhausted={dailySearchRemaining === 0}
              onBuyMore={() => usageMeterRef.current?.openPurchase()}
              fileInputRef={fileInputRef}
              onInputChange={setInput}
              onSend={handleSendWithCollapse}
              onSearch={handleSearch}
              onRequestSample={handleRequestSample}
              onImageChange={handleImageChange}
              onPhotoClick={() => {
                // Mutual exclusion: close voice mode when opening photo options
                if (voiceState.status !== 'disconnected') {
                  toggleVoice();
                }
                if (!masterProfile.consent?.photoAnalysisAccepted) {
                  setShowPhotoConsent(true);
                } else {
                  setShowPhotoOptions(true);
                }
              }}
              voiceState={voiceState}
              isVoiceSupported={isVoiceSupported}
              onVoiceToggle={() => {
                // Mutual exclusion: close photo options when toggling voice
                setShowPhotoOptions(false);
                toggleVoice();
              }}
            />
          </div>

          {/* Two-mode system: Desktop fixed panel OR Compact FAB+BottomSheet */}
          <DesktopProfilePanel
            masterProfile={masterProfile}
            recentChanges={recentChanges}
            profileDiff={profileDiff}
            profileConflicts={profileConflicts}
            coppaBlocked={coppaBlocked}
            isProfileLoading={isProfileLoading}
            profileError={profileError}
            onUndo={handleUndo}
            onUpdateProfile={handleUpdateProfile}
            editorPicks={editorPicks}
            isEditorPicksLoading={isEditorPicksLoading}
            isEditorPicksAffiliate={isEditorPicksAffiliate}
            onRefreshEditorPicks={handleRefreshEditorPicks}
            autoSave={autoSave}
            language={language}
            showAddressFormForSample={showAddressFormForSample}
            onCancelAddressForm={() => setShowAddressFormForSample(false)}
            user={auth.currentUser}
            cartItems={INHOUSE_SELLING_ENABLED ? cartItems : []}
            onAddToCart={INHOUSE_SELLING_ENABLED ? handleAddToCart : undefined}
            onPhotoUpload={() => {
              if (!masterProfile.consent?.photoAnalysisAccepted) {
                setShowPhotoConsent(true);
              } else {
                setShowPhotoOptions(true);
              }
            }}
            wishlistItems={wishlistItems}
            onWishlistToggle={(product) => {
              const wishlistProduct = {
                id: product.id,
                name: product.title || ('name' in product && typeof product.name === 'string' ? product.name : '') || 'Untitled',
                priceUSD: product.priceUSD ?? (typeof product.price === 'number' ? product.price : parseFloat(String(product.price)) || 0),
                image: product.image || '',
                url: product.url || '',
                brand: product.brand,
                source: product.affiliateSource ? 'partner' as const : 'inhouse' as const,
              };
              toggleWishlist(wishlistProduct, 'editor-picks');
            }}
            isInWishlist={isInWishlist}
            onViewDetails={(pick) => {
              setSelectedProduct({
                link: pick.url || '',
                name: pick.name || pick.title,
                image: pick.image,
                price: pick.priceUSD ?? pick.price,
                source: 'inhouse',
                displayLink: pick.brand,
                id: pick.id,
              });
            }}
            onAskAI={handleAskAI}
          />
        </div>

        {/* Inventory Manager (Admin Tool) */}
        {showInventory && (
          <InventoryManager
            user={auth.currentUser}
            language={language}
            onClose={() => setShowInventory(false)}
          />
        )}

        {/* Order History */}
        {INHOUSE_SELLING_ENABLED && showOrderHistory && (
          <OrderHistory
            user={auth.currentUser}
            language={language}
            onClose={() => setShowOrderHistory(false)}
          />
        )}

        {/* Shopping Cart - Responsive: BottomSheet (mobile) OR DesktopPanel (desktop) */}
        {INHOUSE_SELLING_ENABLED && (
        <ResponsiveCart
          isOpen={showCart}
          isCompact={isCompact}
          cartItems={cartItems}
          language={language}
          currency={(masterProfile.identity?.currency === 'KRW' ? 'KRW' : 'USD')}
          onClose={() => setShowCart(false)}
          onUpdateQuantity={handleUpdateCartQuantity}
          onRemoveItem={handleRemoveFromCart}
          onCheckout={handleCartCheckout}
        />
        )}

        {/* Stripe Checkout Form Modal */}
        {INHOUSE_SELLING_ENABLED && showCheckoutForm && checkoutData && (
          <CheckoutForm
            masterProfile={masterProfile}
            checkoutData={checkoutData}
            user={auth.currentUser}
            language={language}
            onSuccess={(orderId) => {
              setShowCheckoutForm(false);
              // Clear cart in both local state AND Firebase
              clearCart();
              setToastMessage(language === 'en'
                ? `✅ Payment successful! Order #${orderId}`
                : `✅ 결제 성공! 주문 #${orderId}`
              );
            }}
            onCancel={() => {
              setShowCheckoutForm(false);
              setShowCart(true);
            }}
          />
        )}

        {/* Language/Region Setup Modal - first-time blocking setup */}
        {showLangRegionSetup && (
          <LanguageRegionModal
            currentLanguage={language}
            detectedRegion={detectUserRegion(undefined)}
            onConfirm={handleLangRegionConfirm}
            skipLanguageStep={(() => { try { return localStorage.getItem('heseda-ai-language-explicit') === 'true'; } catch { return false; } })()}
          />
        )}

        {/* Welcome Modal - first-time user onboarding */}
        {showWelcome && (
          <WelcomeModal
            onUploadPhoto={async (interests) => {
              setShowWelcome(false);
              try { if (userId) localStorage.setItem(`heseda_onboarding_completed_${userId}`, 'true'); } catch { /* */ }
              if (interests) await handleSend(interests);
              if (!masterProfile.consent?.photoAnalysisAccepted) {
                setShowPhotoConsent(true);
              } else {
                setShowPhotoOptions(true);
              }
            }}
            onStartChat={async (interests) => {
              setShowWelcome(false);
              try { if (userId) localStorage.setItem(`heseda_onboarding_completed_${userId}`, 'true'); } catch { /* */ }
              if (interests) await handleSend(interests);
            }}
          />
        )}

        {/* Settings Modal */}
        {showSettings && (
          <SettingsModal
            language={language}
            masterProfile={masterProfile}
            autoSave={autoSave}
            autoSearchEnabled={autoSearchEnabled}
            useRagRecommendation={useRagRecommendation}
            monetizationMode={monetizationMode}
            analysisProvider={analysisProvider}
            hydrateProfile={hydrateProfile}
            onLanguageChange={handleLanguageChange}
            onAnalysisProviderChange={setAnalysisProvider}
            onAutoSaveChange={setAutoSave}
            onAutoSearchChange={setAutoSearchEnabled}
            onRagRecommendationChange={setUseRagRecommendation}
            onMonetizationModeChange={setMonetizationMode}
            onShowInventory={() => setShowInventory(true)}
            onShowOrderHistory={() => setShowOrderHistory(true)}
            onClose={() => setShowSettings(false)}
            buildTimestamp={buildTimestamp}
          />
        )}

        {/* Feedback Form Modal */}
        {showFeedback && (
          <FeedbackForm
            onClose={() => setShowFeedback(false)}
            onSuccess={() => setFeedbackSubmittedThisSession(true)}
            recentMessages={messages.slice(-5).map(m => ({
              sender: m.sender,
              text: m.text,
              timestamp: typeof m.timestamp === 'string' ? m.timestamp : m.timestamp?.toISOString?.() ?? '',
            }))}
          />
        )}

        {/* Photo Consent Modal - Still needed for COPPA compliance when uploading photos */}
        {showPhotoConsent && (
          <PhotoConsentModal
            language={language}
            onAccept={handlePhotoConsentAccept}
            onCancel={() => setShowPhotoConsent(false)}
          />
        )}

        {/* Photo Upload Options Modal - Choose camera or file upload */}
        <PhotoUploadOptions
          isOpen={showPhotoOptions}
          onClose={() => setShowPhotoOptions(false)}
          onSelectCamera={async () => {
            setShowPhotoOptions(false);
            // Request camera permission first
            const hasPermission = await requestCameraPermission();
            if (hasPermission) {
              setShowFaceGuideline(true);
            }
          }}
          onSelectFile={() => {
            setShowPhotoOptions(false);
            fileInputRef.current?.click();
          }}
          language={language}
        />

        {/* Face Guideline Modal - Camera preview with face positioning guide */}
        <FaceGuidelineModal
          isOpen={showFaceGuideline}
          onClose={() => setShowFaceGuideline(false)}
          onCapture={handleCameraCapture}
        />
      </div>
    ) : (
      <LoginForm
        loginError={loginError}
        isSigningIn={isSigningIn}
        onGoogleSignIn={handleGoogleSignIn}
      />
      )}

      {/* Compact mode: Floating Action Button + Bottom Sheet - Only show when logged in AND in compact mode */}
      {isLoggedIn && isCompact && (
        <MobileProfileFAB
          isOpen={isProfileOpen}
          onOpen={() => setIsProfileOpen(true)}
          onClose={() => setIsProfileOpen(false)}
          masterProfile={masterProfile}
          recentChanges={recentChanges}
          profileDiff={profileDiff}
          profileConflicts={profileConflicts}
          coppaBlocked={coppaBlocked}
          isProfileLoading={isProfileLoading}
          profileError={profileError}
          onUndo={handleUndo}
          onUpdateProfile={handleUpdateProfile}
          editorPicks={editorPicks}
          isEditorPicksLoading={isEditorPicksLoading}
          isEditorPicksAffiliate={isEditorPicksAffiliate}
          onRefreshEditorPicks={handleRefreshEditorPicks}
          autoSave={autoSave}
          language={language}
          showAddressFormForSample={showAddressFormForSample}
          onCancelAddressForm={() => setShowAddressFormForSample(false)}
          user={auth.currentUser}
          cartItems={INHOUSE_SELLING_ENABLED ? cartItems : []}
          onAddToCart={INHOUSE_SELLING_ENABLED ? handleAddToCart : undefined}
          onPhotoUpload={() => {
            if (!masterProfile.consent?.photoAnalysisAccepted) {
              setShowPhotoConsent(true);
            } else {
              setShowPhotoOptions(true);
            }
          }}
          onViewDetails={(pick) => {
            setSelectedProduct({
              link: pick.url || '',
              name: pick.name || pick.title,
              image: pick.image,
              price: pick.priceUSD ?? pick.price,
              source: 'inhouse',
              displayLink: pick.brand,
              id: pick.id,
            });
          }}
          onAskAI={handleAskAI}
        />
      )}

      {/* Toast Notification */}
      <ToastNotification message={toastMessage} />

      {/* Product Experience Panel - View Details / My Picks */}
      <ProductExperiencePanel
        panelId="wishlist-panel"
        isOpen={!!selectedProduct || showWishlistPanel}
        onClose={() => {
          setSelectedProduct(null);
          setShowWishlistPanel(false);
        }}
        product={selectedProduct}
        wishlistItems={wishlistItems}
        onAddToCart={INHOUSE_SELLING_ENABLED ? handleAddToCart : undefined}
        onWishlistToggle={toggleWishlist}
        onRemoveFromWishlist={removeFromWishlist}
        isInWishlist={isInWishlist}
        language={language}
      />

      {/* Notification Permission Prompt - Native platforms only (Android/iOS) */}
      <NotificationPromptModal
        isOpen={showNotificationPrompt}
        onEnable={handleEnableNotifications}
        onDismiss={handleDismissNotificationPrompt}
      />
    </>
  );
}

export default App;
